import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  LocalHospital,
  Medication,
  Science,
  Assignment,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";

const QuickStats = ({ patientId }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Fetch unified EHR data to get counts
  const {
    data: ehrData,
    isLoading,
    isError,
  } = useFetchObjects(
    patientId ? ["unified-ehr", patientId] : null,
    patientId ? `patients/${patientId}/unified-ehr/` : "",
    token
  );

  if (isLoading) {
    return null; // Don't show loading state for stats
  }

  if (isError || !ehrData) {
    return null; // Don't show error state for stats
  }

  const timeline = ehrData.timeline || [];

  // Count different types
  const encounters = timeline.filter((item) => item.type === "encounter").length;
  const prescriptions = timeline.filter((item) => item.type === "prescription").length;
  const labResults = timeline.filter((item) => item.type === "laboratory").length;
  const admissions = timeline.filter(
    (item) => item.type === "ipd_admission" || item.type === "icu_admission"
  ).length;

  const stats = [
    {
      label: t("profile.encounters") || "Encounters",
      value: encounters,
      icon: <Assignment color="primary" />,
      color: "#1976d2",
    },
    {
      label: t("profile.prescriptions") || "Prescriptions",
      value: prescriptions,
      icon: <Medication color="success" />,
      color: "#2e7d32",
    },
    {
      label: t("profile.labResults") || "Lab Results",
      value: labResults,
      icon: <Science color="warning" />,
      color: "#ed6c02",
    },
    {
      label: t("profile.admissions") || "Admissions",
      value: admissions,
      icon: <LocalHospital color="error" />,
      color: "#d32f2f",
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t("profile.quickStats") || "Quick Statistics"}
      </Typography>
      <Grid container spacing={2}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card
              sx={{
                height: "100%",
                borderLeft: `4px solid ${stat.color}`,
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ opacity: 0.7 }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default QuickStats;
